import { createElement as h, Fragment } from 'react'
import type { ReactNode } from 'react'
import type { IToken } from 'types'

// Pure logic for a confirmed Shopify store switch — no JSX / module side-effects, so it can be
// unit-tested directly. The hook (useShopifyStoreSwitch) wires in the real dependencies.

export const switchDoneKey = (toAccount: string) => `shopify_switch_done_${toAccount}`

// The outgoing store reads as "being removed" (light red), the incoming one as "taking
// over" (green), so the merchant can't misread the direction of the switch.
const oldStore = (name: string) =>
  h('strong', { style: { color: '#e57373' } }, `"${name}"`)
const newStore = (name: string) =>
  h('strong', { style: { color: '#388e3c' } }, `"${name}"`)

// Rendered by ConfirmationMessage with white-space: pre-line, so \n\n produces real
// paragraph breaks instead of one wall of text. Children are passed variadically (not as
// an array), so React doesn't require keys here.
export const buildSwitchPrompt = (fromShop: string, toShop: string): ReactNode =>
  h(Fragment, null,
    "You're about to switch this organization's Shopify store from ",
    oldStore(fromShop), ' to ', newStore(toShop), '.\n\n',
    oldStore(fromShop), ' will be deactivated and ', newStore(toShop), ' will take over. If ',
    oldStore(fromShop), ' has an active subscription, it will be cancelled with a prorated ',
    "credit and you'll subscribe again on ", newStore(toShop), '.\n\n',
    'Want to keep both stores? Connect ', newStore(toShop), ' in a separate organization ',
    'instead — each store is billed on its own organization.\n\n',
    'Switch to ', newStore(toShop), ' now?',
  )

export interface SwitchParams {
  toAccount: string
  fromShop: string
  toShop: string
}

export type SwitchOutcome = 'cancelled' | 'failed' | 'switched'

export interface CompleteSwitchDeps {
  slug: string
  orgId: number
  token: IToken
  confirm: (message: ReactNode, opts?: { confirmLabel?: string }) => Promise<boolean>
  switchStore: (args: { organizationId: number; token: IToken; toAccount: string; confirm: boolean }) => Promise<any>
  redirect: (path: string) => void
  refreshAccounts: (orgId: number) => void
  onError: (message: string) => void
  storage: Pick<Storage, 'getItem' | 'setItem'>
  track: (event: any) => void
}

/**
 * Apply a confirmed store switch. Persists "handled" ONLY after the switch actually succeeds — on
 * cancel or failure nothing is persisted, so reconnecting the same store re-offers the switch
 * instead of stranding the user. Returns what happened (for callers/tests).
 */
export async function completeShopifyStoreSwitch(
  params: SwitchParams,
  deps: CompleteSwitchDeps,
): Promise<SwitchOutcome> {
  const { toAccount, fromShop, toShop } = params
  const { slug, orgId, token, confirm, switchStore, redirect, refreshAccounts, onError, storage, track } = deps

  const ok = await confirm(buildSwitchPrompt(fromShop, toShop), { confirmLabel: 'Switch store' })
  if (!ok) return 'cancelled' // user declined — do NOT persist, so reconnect re-offers it

  let resp: any = null
  try {
    resp = await switchStore({ organizationId: orgId, token, toAccount, confirm: true })
  } catch (e: any) {
    console.error('Failed to apply Shopify store switch:', e)
    // Surface the failure — this is billing-critical. The backend crafts an actionable detail for
    // recoverable cases (e.g. old_charge_cancel_failed: "nothing was changed, reconnect to retry");
    // fall back to a generic message otherwise. Nothing is persisted, so reconnecting re-offers it.
    const detail = e?.response?.data?.detail
    onError(
      typeof detail === 'string' && detail
        ? detail
        : `We couldn't switch your Shopify store to "${toShop}".\n\nNothing was changed — reconnect the store to try again.`,
    )
    return 'failed'
  }

  // Succeeded — persist so a remount / back-button to a stale URL can't re-run this same switch.
  try { storage.setItem(switchDoneKey(toAccount), '1') } catch (e) { /* ignore storage errors */ }
  track({ dataLayer: { event: 'shopify_store_switched', is_custom_track: true } })

  // If the old store's subscription was cancelled, route to subscribe on the NEW store. The
  // Products page enters the Shopify billing flow (not Stripe) only on ?shopify=true&shop=<domain>.
  // switched=true rides along so the post-purchase page knows the org's existing data belongs to
  // the OLD store and waits for the new store's first sync instead of showing it as "ready".
  if (resp?.needs_resubscribe) {
    const shop = resp?.shop_domain || (toShop ? `${toShop}.myshopify.com` : '')
    const query = shop ? `?shopify=true&shop=${encodeURIComponent(shop)}&switched=true` : '?shopify=true&switched=true'
    redirect(`/organization/${slug}/billing/products${query}`)
  } else {
    refreshAccounts(orgId)
  }
  return 'switched'
}
